import type { StateCreator } from 'zustand';
import { generateUuid } from '@/lib/uuid';
import { parseTimestamp } from '@/utils/dateFormat';
import type { DispatchPrintTab, DispatchReprintRecord } from '@/types/invoice';
import type { TabItem } from '@/types';
import type { DispatchReprintsStore } from './dispatchReprints.types';

const revivePrintData = (raw: DispatchPrintTab): DispatchPrintTab => ({
  ...raw,
  createdAt: parseTimestamp(raw.createdAt) ?? new Date(),
  items: (raw.items ?? []).map((item: TabItem) => ({
    ...item,
    createdAt: parseTimestamp(item.createdAt),
  })),
});

export const reviveDispatchReprintRecord = (raw: DispatchReprintRecord): DispatchReprintRecord => ({
  ...raw,
  createdAt: parseTimestamp(raw.createdAt) ?? new Date(),
  printData: revivePrintData(raw.printData),
});

export const createDispatchReprintsActions = (
  set: Parameters<StateCreator<DispatchReprintsStore>>[0],
  get: Parameters<StateCreator<DispatchReprintsStore>>[1],
): Pick<DispatchReprintsStore, 'addRecord' | 'getForTab' | 'replaceForTab' | 'clearForTab'> => ({
  addRecord: (organizationId, tabId, printData) => {
    const createdAt = parseTimestamp(printData.createdAt) ?? new Date();
    const record: DispatchReprintRecord = {
      id: generateUuid(),
      organizationId,
      tabId,
      printData: revivePrintData(printData),
      createdAt,
    };

    const existing = get().byTabId[tabId] ?? [];
    set({
      byTabId: {
        ...get().byTabId,
        [tabId]: [...existing, record],
      },
    });
  },

  getForTab: (tabId) =>
    (get().byTabId[tabId] ?? []).map(reviveDispatchReprintRecord),

  replaceForTab: (tabId, records) => {
    const revived = records.map(reviveDispatchReprintRecord);
    if (revived.length === 0) {
      const { [tabId]: _removed, ...rest } = get().byTabId;
      set({ byTabId: rest });
      return;
    }
    set({
      byTabId: {
        ...get().byTabId,
        [tabId]: revived,
      },
    });
  },

  clearForTab: (tabId) => {
    const { [tabId]: _removed, ...rest } = get().byTabId;
    set({ byTabId: rest });
  },
});
